import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'

import {
  Card,
  CARD_THEME,
  Chip,
  CHIP_TYPES,
  Button,
  BUTTON_THEME,
  Icon,
  IconButton,
  ICON_TYPES,
  BarLoader as Loader,
  Divider,
} from '@st-operations/uikit/src/v2/index.js'
import pretty from '@st-operations/uikit/lib/pretty.js'
import format from '@st-operations/uikit/lib/format.js'

import FilterTopicsButton from '../filter-topics-button/index.jsx'
import SortTopicsButton, { SORT_TYPES } from '../sort-topics-button/index.jsx'

import { useCollapsibleElement } from '_hooks'

import './styles.scss'

const TYPES = {
  RECOMMENDED: 'Recommended Topics',
  SIMILAR: 'Similar Topics',
}

const formatTopicDisplayName = (name, size, translatedTopics) => {
  const translation = translatedTopics.find((topic) => topic.keyword === name)
  if (translation) {
    return `${pretty(name)} (${translation.translation}) ${format('metric', size)}`
  }
  return `${pretty(name)} ${format('metric', size)}`
}

const TopicsCard = ({
  selectedTopic,
  title,
  loading,
  items,
  onAddTopic,
  onAddAll,
  translatedTopics,
}) => {
  const { isOpen, collapsibleHeight, collapsibleRef, TriggerCollapsible } = useCollapsibleElement([
    items,
  ])
  const [filteredItems, setFilteredItems] = useState([])

  useEffect(() => {
    setFilteredItems(items)
  }, [items])

  const handleSortItems = (type) => {
    switch (type) {
      case SORT_TYPES.ALPHABETICAL:
        setFilteredItems((prevItems) =>
          [...prevItems].sort((itemA, itemB) => (itemA.name > itemB.name ? 1 : -1))
        )
        break
      case SORT_TYPES.SIZE:
      default:
        setFilteredItems((prevItems) =>
          [...prevItems].sort((itemA, itemB) =>
            itemA.sizes?.sharethis < itemB.sizes?.sharethis ? 1 : -1
          )
        )
        break
    }

    return
  }

  const handleAddAll = (event) => {
    onAddAll({ event, items: filteredItems })
  }

  const handleFilterSize = (minimumSize) => {
    setFilteredItems(items.filter(({ sizes }) => sizes?.sharethis > minimumSize))
  }

  const Description = () => (
    <If condition={selectedTopic.name}>
      <span className="topics-card__card-description">
        Based on &quot;{pretty(selectedTopic.name)}&quot;
      </span>
    </If>
  )

  return (
    <Card theme={CARD_THEME.NO_SHADOW} className="topics-card__card">
      <header className="topics-card__header">
        <div className="topics-card__container">
          <h1 className="topics-card__card-title">
            <span>{title}:&nbsp;</span>
            <Description />
          </h1>
        </div>
        <SortTopicsButton onSort={handleSortItems} />
        <FilterTopicsButton onFilter={handleFilterSize} />
        <Divider vertical className="topics-card__divider" />
        <TriggerCollapsible />
      </header>
      <div
        style={{ maxHeight: collapsibleHeight }}
        ref={collapsibleRef}
        className={classnames('topics-card__content', {
          'topics-card__content--open': isOpen,
        })}
        aria-hidden={!isOpen}
      >
        <div className="topics-card__chips">
          <Choose>
            <When condition={loading}>
              <Loader className="topics-card__loader" />
            </When>
            <When condition={filteredItems.length > 0}>
              <For each="item" of={filteredItems}>
                <Chip
                  key={`${item.name}-${item._id}`}
                  value={formatTopicDisplayName(item.name, item.sizes.sharethis, translatedTopics)}
                  onClick={onAddTopic(item)}
                  onButtonClick={onAddTopic(item)}
                  small
                  hasAddIcon
                  type={CHIP_TYPES.FILL}
                  className="topics-card__chip"
                  iconStyle={{ fill: 'currentColor' }}
                />
              </For>
            </When>
            <When condition={items.length === 0 && selectedTopic.name}>
              <p className="topics-card__empty-topic">
                No {title === TYPES.RECOMMENDED ? 'recommended' : 'similar topics'} for the selected
                topic
              </p>
            </When>
            <When condition={filteredItems.length === 0 && items.length > 0}>
              <p className="topics-card__empty-topic">You&apos;ve filtered all topics</p>
            </When>
            <When condition={selectedTopic.name}>
              <p className="topics-card__empty-topic">No more topics for the selected topic</p>
            </When>
            <Otherwise>
              <p className="topics-card__empty-topic">No topic selected</p>
            </Otherwise>
          </Choose>
        </div>
        <If condition={filteredItems.length > 0}>
          <Button onClick={handleAddAll} theme={BUTTON_THEME.GRAY} className="topics-card__add-all">
            <Icon icon="plus2" type="fill" width="16px" height="16px" color="#3D3D3D" />
            Add all similar topics
          </Button>
        </If>
      </div>
    </Card>
  )
}

TopicsCard.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape()),
  loading: PropTypes.bool,
  onAddTopic: PropTypes.func,
  onAddAll: PropTypes.func,
  selectedTopic: PropTypes.shape({
    name: PropTypes.string,
    _id: PropTypes.string,
  }),
  title: PropTypes.string,
}

TopicsCard.defaultProps = {
  items: [],
  loading: false,
  onAddTopic: () => {},
  onAddAll: () => {},
  selectedTopic: { name: '', _id: '' },
  title: '',
}

export default React.memo(TopicsCard)
